import { useEffect, useMemo, useState } from 'react';
import { CrossIcon, Plus } from 'assets/icons';
import Button from 'components/button';

/**
 * « Plus de filtres »: the side panel of a list's filters. Each filter folds
 * open on its own control (`renderFilterByKey`); the active ones come first,
 * with what they keep (`getFilterDisplayValue`).
 */
const FilterPanel = ({
  isOpen,
  onClose,
  activeFilterCount,
  filters,
  renderFilterByKey,
  getFilterDisplayValue,
  clearFilterByKey,
  onReset,
  onApply,
}) => {
  const [expandedFilter, setExpandedFilter] = useState(null);

  useEffect(() => {
    if (!isOpen) setExpandedFilter(null);
  }, [isOpen]);

  const sortedFilters = useMemo(
    () =>
      [...filters].sort((a, b) => {
        const aActive = getFilterDisplayValue(a.key) !== null;
        const bActive = getFilterDisplayValue(b.key) !== null;
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        return 0;
      }),
    [filters, getFilterDisplayValue]
  );

  const handleReset = () => {
    setExpandedFilter(null);
    onReset();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-gray-900 opacity-50 transition-opacity duration-300" />
      <div
        className="absolute right-0 top-0 h-full w-full max-w-[520px] bg-white shadow-xl z-50 flex flex-col animate-slideR"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between p-5 gap-5">
          <h3 className="ft-lg text-[#202124] font-semibold my-auto flex-1 min-w-0 flex items-center gap-2">
            Plus de filtres
            {activeFilterCount > 0 && (
              <div className="bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px] ft-sm">
                {activeFilterCount}
              </div>
            )}
          </h3>
          <button type="button" className="flex items-center justify-center border rounded p-1" onClick={onClose}>
            <CrossIcon wh={20} color="#111827" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-2">
            {sortedFilters.map((filter, index) => {
              const isExpanded = expandedFilter === filter.key;
              const displayValue = getFilterDisplayValue(filter.key);
              const hasValue = displayValue !== null;
              const prevHasValue =
                index > 0 && getFilterDisplayValue(sortedFilters[index - 1].key) !== null;
              const isFirstInactive = index > 0 && !hasValue && prevHasValue;

              if (isExpanded) {
                return (
                  <div
                    key={filter.key}
                    className="border border-yellow-400 rounded-md bg-white sh-sm overflow-visible"
                  >
                    <div className="flex items-center justify-between px-4 py-3 bg-yellow-50 rounded-t-md">
                      <span className="ft-sm font-medium text-gray-900">{filter.label}</span>
                      <button
                        type="button"
                        onClick={() => setExpandedFilter(null)}
                        className="p-0.5 rounded hover:bg-yellow-100 transition-colors"
                      >
                        <CrossIcon wh={14} color="#374151" />
                      </button>
                    </div>
                    <div className="px-4 py-3">
                      {renderFilterByKey(filter.key, { fullWidth: true })}
                    </div>
                  </div>
                );
              }

              if (isFirstInactive) {
                return (
                  <div key={`divider-${filter.key}`}>
                    <div className="my-2 border-t border-gray-200" />
                    <button
                      type="button"
                      onClick={() => setExpandedFilter(filter.key)}
                      className="flex items-center justify-between w-full p-3 bg-white border border-gray-200 rounded-md hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                    >
                      <span className="ft-sm text-gray-600">{filter.label}</span>
                      <Plus wh={16} color="#9CA3AF" />
                    </button>
                  </div>
                );
              }

              if (hasValue) {
                return (
                  <div
                    key={filter.key}
                    className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md cursor-pointer hover:bg-yellow-100 transition-colors"
                    onClick={() => setExpandedFilter(filter.key)}
                  >
                    <div className="flex flex-col min-w-0 pr-2">
                      <span className="ft-sm font-medium text-gray-900">{filter.label}</span>
                      <span className="text-xs text-yellow-700 truncate">{displayValue}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilterByKey(filter.key);
                      }}
                      className="p-1 rounded hover:bg-yellow-200 transition-colors shrink-0"
                    >
                      <CrossIcon wh={14} color="#374151" />
                    </button>
                  </div>
                );
              }

              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setExpandedFilter(filter.key)}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                >
                  <span className="ft-sm text-gray-600">{filter.label}</span>
                  <Plus wh={16} color="#9CA3AF" />
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex gap-3">
          <Button color="SECONDARY" size="SM" className="flex-1" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button color="PRIMARY" size="SM" className="flex-1" onClick={onApply}>
            Appliquer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
